import React, { useState, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { DASTicketDetails } from '../../../../api/types/dasTickets.types';
import { TicketBasicInfo } from './shared/TicketBasicInfo';
import { TicketAttachments } from './shared/TicketAttachments';
import { TicketHistory } from './shared/TicketHistory';
import { TicketApprovalFlow } from './shared/TicketApprovalFlow';
import { TicketRelated } from './shared/TicketRelated';
import { TicketConsolidatedItems } from './shared/TicketConsolidatedItems';

interface ActiveTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: DASTicketDetails;
  onTakeAction: () => void;
  onResubmit?: () => void;
  onCancel?: () => void;
}

type TabId = 'details' | 'approval-flow' | 'history' | 'attachments' | 'related' | 'consolidated';

interface TabConfig {
  id: TabId;
  label: string;
  icon: React.ComponentType<{ size?: number; style?: React.CSSProperties }>;
  badge?: number;
}

const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const ActiveTicketModal: React.FC<ActiveTicketModalProps> = ({
  isOpen,
  onClose,
  ticket,
  onTakeAction,
  onResubmit,
  onCancel,
}) => {
  const [activeTab, setActiveTab] = useState<TabId>('details');
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const actionMenuRef = useRef<HTMLDivElement>(null);

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target as Node)) {
        setActionMenuOpen(false);
      }
    };

    if (actionMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [actionMenuOpen]);

  if (!isOpen) return null;

  // Tab configuration for active tickets
  const tabs: TabConfig[] = [
    { id: 'details', label: 'Details', icon: IconSystem.file },
    { id: 'approval-flow', label: 'Approval Flow', icon: IconSystem.users },
    { id: 'history', label: 'History', icon: IconSystem.clock },
    { id: 'attachments', label: 'Attachments', icon: IconSystem.file, badge: ticket.attachments?.length || 0 },
    { id: 'related', label: 'Related', icon: IconSystem.link },
    // Only show Consolidated Items tab for consolidated requests
    ...(ticket.isConsolidatedRequest ? [
      { id: 'consolidated' as TabId, label: 'Consolidated Items', icon: IconSystem.list, badge: ticket.consolidatedItemCount || 0 },
    ] : []),
  ];

  // Handle escape key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  // Check if ticket is returned and user is the one it was returned to
  const isReturned = ticket.currentStage === 'Returned';
  const isCreator = ticket.yourRole === 'Creator';

  // Check if user can take action (as approver/recommender)
  // IMPORTANT: When a request is returned, only the returned-to user can take action (resubmit/cancel)
  // Pre-approvers and other approvers should NOT be able to take action on returned requests
  const canTakeAction = ticket.yourActionStatus === 'Pending – Your action needed' && !isReturned;

  // The user who the request was returned to can see resubmit/cancel options
  // This could be the creator OR a previous approver who needs to provide clarification
  // The yourActionStatus is set to 'Pending – Your action needed' for the returned-to user in the mapper
  const canResubmitOrCancel = isReturned && ticket.yourActionStatus === 'Pending – Your action needed';

  // Render Details Tab (Main tab for active tickets)
  const renderDetailsTab = () => {
    return (
      <div style={{ padding: '1.5rem' }}>
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {/* Your Action Status Card - Prominent for active tickets */}
          {canTakeAction && (
            <div
              style={{
                background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%)',
                borderRadius: '0.75rem',
                padding: '1.5rem',
                border: '2px solid #f59e0b',
                boxShadow: '0 4px 6px rgba(245, 158, 11, 0.1)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '1rem',
                }}
              >
                <div>
                  <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.125rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                    Action Required
                  </h3>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    This ticket is pending your approval or action
                  </p>
                </div>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 1.25rem',
                    background: '#f59e0b',
                    color: 'white',
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    borderRadius: '0.5rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  <IconSystem.clock size={18} />
                  PENDING
                </div>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem',
                  marginBottom: '1rem',
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: '0.75rem',
                      color: 'var(--text-secondary)',
                      marginBottom: '0.25rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                    }}
                  >
                    Your Role
                  </div>
                  <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {ticket.yourRole}
                  </div>
                </div>
                <div>
                  <div
                    style={{
                      fontSize: '0.75rem',
                      color: 'var(--text-secondary)',
                      marginBottom: '0.25rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                    }}
                  >
                    Pending Since
                  </div>
                  <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {formatDate(ticket.pendingSince)} ({ticket.pendingAge} days)
                  </div>
                </div>
              </div>

              <button
                onClick={onTakeAction}
                style={{
                  width: '100%',
                  padding: '0.75rem 1.5rem',
                  background: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#d97706';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f59e0b';
                }}
              >
                <IconSystem.check size={20} />
                Take Action
              </button>
            </div>
          )}

          {/* Returned Request Card - For creators of returned requests */}
          {canResubmitOrCancel && (
            <div
              style={{
                background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%)',
                borderRadius: '0.75rem',
                padding: '1.5rem',
                border: '2px solid #ef4444',
                boxShadow: '0 4px 6px rgba(239, 68, 68, 0.1)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.125rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                    Request Returned
                  </h3>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                    This request has been returned. Review the feedback, make changes, and use the &quot;Take Action&quot; button below to resubmit or cancel.
                  </p>
                </div>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 1.25rem',
                    background: '#ef4444',
                    color: 'white',
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    borderRadius: '0.5rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    flexShrink: 0,
                  }}
                >
                  <IconSystem.alert size={18} />
                  RETURNED
                </div>
              </div>
            </div>
          )}

          {/* Status & Timeline Card */}
          <div
            style={{
              background: 'var(--bg-secondary)',
              borderRadius: '0.5rem',
              padding: '1.25rem',
              border: '1px solid var(--border-color)',
            }}
          >
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
              Current Status
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                  Current Stage
                </div>
                <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                  {ticket.currentStage}
                </div>
              </div>
              {ticket.ticketAge !== undefined && (
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                    Total Ticket Age
                  </div>
                  <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                    {ticket.ticketAge} days
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Basic Information */}
          <TicketBasicInfo ticket={ticket} />

          {/* Additional Information */}
          {(ticket.nextApprover || ticket.comments) && (
            <div
              style={{
                background: 'var(--bg-secondary)',
                borderRadius: '0.5rem',
                padding: '1.25rem',
                border: '1px solid var(--border-color)',
              }}
            >
              <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                Additional Information
              </h3>
              <div
                style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}
              >
                {ticket.nextApprover && (
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                      Next Approver
                    </div>
                    <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                      {ticket.nextApprover}
                    </div>
                  </div>
                )}
              </div>
              {ticket.comments && (
                <div style={{ marginTop: '1rem' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                    Comments
                  </div>
                  <div
                    style={{
                      fontSize: '0.875rem',
                      color: 'var(--text-primary)',
                      lineHeight: 1.6,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {ticket.comments}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'details':
        return renderDetailsTab();
      case 'approval-flow':
        return <TicketApprovalFlow approvalHierarchy={ticket.approvalHierarchy || []} ticketStatus={ticket.currentStage} />;
      case 'history':
        return <TicketHistory workflowHistory={ticket.workflowHistory || []} />;
      case 'attachments':
        return <TicketAttachments attachments={ticket.attachments || []} />;
      case 'related':
        return <TicketRelated relatedTickets={ticket.relatedTickets || []} relatedTicketDetails={ticket.relatedTicketDetails} />;
      case 'consolidated':
        return (
          <TicketConsolidatedItems
            consolidatedRequestId={ticket.id.toString()}
            itemCount={ticket.consolidatedItemCount}
            totalValue={ticket.consolidatedTotalValue}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '1rem',
      }}
      onKeyDown={handleKeyDown}
    >
      <div
        style={{
          background: 'var(--bg-primary)',
          borderRadius: '0.5rem',
          width: '100%',
          maxWidth: '1200px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '1.5rem',
            borderBottom: '1px solid var(--border-color)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                {ticket.ticketNo}
              </h2>
              <span
                style={{
                  padding: '0.25rem 0.625rem',
                  background:
                    ticket.priority === 'Urgent'
                      ? '#dc2626'
                      : ticket.priority === 'High'
                      ? '#f59e0b'
                      : ticket.priority === 'Medium'
                      ? '#3b82f6'
                      : '#64748b',
                  color: 'white',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  borderRadius: '0.25rem',
                  textTransform: 'uppercase',
                }}
              >
                {ticket.priority}
              </span>
            </div>
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              {ticket.subject}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              color: 'var(--text-secondary)',
            }}
            title="Close"
          >
            <IconSystem.x size={24} />
          </button>
        </div>

        {/* Tab Navigation */}
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid var(--border-color)',
            padding: '0 1.5rem',
            overflowX: 'auto',
            overflowY: 'hidden',
            flexShrink: 0,
          }}
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '1rem 1.5rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: activeTab === tab.id ? 'var(--primary)' : 'var(--text-secondary)',
                  borderBottom: activeTab === tab.id ? '2px solid var(--primary)' : '2px solid transparent',
                  fontWeight: activeTab === tab.id ? 600 : 400,
                  fontSize: '0.875rem',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap',
                }}
              >
                <Icon size={16} />
                {tab.label}
                {tab.badge !== undefined && tab.badge > 0 && (
                  <span
                    style={{
                      background: activeTab === tab.id ? 'var(--primary)' : 'var(--text-muted)',
                      color: 'white',
                      padding: '0.125rem 0.5rem',
                      borderRadius: '1rem',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                    }}
                  >
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div style={{ flex: 1, overflow: 'auto' }}>{renderTabContent()}</div>

        {/* Footer */}
        <div
          style={{
            padding: '1rem 1.5rem',
            borderTop: '1px solid var(--border-color)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottomLeftRadius: '0.5rem',
            borderBottomRightRadius: '0.5rem',
          }}
        >
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            Last updated: {formatDate(ticket.lastActionDate || ticket.createdAt)}
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            {/* Take Action button - for approvers with pending action */}
            {canTakeAction && (
              <button
                onClick={onTakeAction}
                style={{
                  padding: '0.5rem 1.5rem',
                  background: 'var(--primary)',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                <IconSystem.check size={16} />
                Take Action
              </button>
            )}

            {/* Take Action dropdown - for creators of returned tickets */}
            {canResubmitOrCancel && (
              <div ref={actionMenuRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => setActionMenuOpen(!actionMenuOpen)}
                  style={{
                    padding: '0.5rem 1.5rem',
                    background: 'var(--primary)',
                    border: 'none',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '0.9';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '1';
                  }}
                >
                  <IconSystem.edit size={16} />
                  Take Action
                  <IconSystem.chevronDown size={14} style={{ marginLeft: '0.25rem' }} />
                </button>

                {/* Dropdown menu */}
                {actionMenuOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '100%',
                      right: 0,
                      marginBottom: '0.5rem',
                      background: 'var(--bg-primary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '0.5rem',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                      minWidth: '180px',
                      overflow: 'hidden',
                      zIndex: 1000,
                    }}
                  >
                    {onResubmit && (
                      <button
                        onClick={() => {
                          setActionMenuOpen(false);
                          onResubmit();
                        }}
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem',
                          background: 'transparent',
                          border: 'none',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: 'var(--text-primary)',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          textAlign: 'left',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'var(--bg-secondary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <IconSystem.refresh size={16} style={{ color: '#10b981' }} />
                        Resubmit
                      </button>
                    )}
                    {onCancel && (
                      <button
                        onClick={() => {
                          setActionMenuOpen(false);
                          onCancel();
                        }}
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem',
                          background: 'transparent',
                          border: 'none',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: '#dc2626',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          textAlign: 'left',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(220, 38, 38, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <IconSystem.x size={16} style={{ color: '#dc2626' }} />
                        Cancel Request
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            <button
              onClick={onClose}
              style={{
                padding: '0.5rem 1.5rem',
                background: 'transparent',
                border: '1px solid var(--border-color)',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'var(--text-primary)',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--bg-secondary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActiveTicketModal;
